import { ApolloError } from '@apollo/client'
import { Description, Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ComponentProps, ElementType, PropsWithChildren, createContext, useContext } from 'react'

import Button from '@/modules/shared/components/button/Button'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import { CloseIcon } from '@/modules/shared/icons/CloseIcon'
import { classNames } from '@/modules/shared/utils/classNames'

export interface ModalProps extends PropsWithChildren {
  showModal: boolean
  onCloseModal: () => void
  dataTestId?: string
  loading?: boolean
  error?: ApolloError
}

interface ModalContextType {
  loading?: boolean
  error?: ApolloError
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

function ModalComponent({ showModal, onCloseModal, dataTestId, children, loading, error }: ModalProps) {
  const contextValue = { loading, error }

  return (
    <Dialog as="div" className="relative z-40" onClose={onCloseModal} data-testid={dataTestId} open={showModal} unmount>
      <DialogBackdrop
        className="fixed inset-0 bg-gray-900/25 transition data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in"
        transition
      />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>
        </div>
      </div>
    </Dialog>
  )
}

interface ModalTitleProps {
  title: string
  onCloseModal?: () => void
  testId?: string
  className?: string
  as?: ElementType
}

function ModalTitle({ title, onCloseModal, testId, className, as = 'h2' }: ModalTitleProps) {
  return (
    <DialogTitle
      className={classNames('flex w-full items-center justify-between border-b px-5 py-3', className)}
      data-testid={testId}
      as={as}
    >
      <span className="text-gray-800">{title}</span>
      {onCloseModal && (
        <Button type="button" className="h-11 rounded-md bg-gray-100 px-3 focus:outline-none" onClick={onCloseModal}>
          <CloseIcon className="size-5" />
        </Button>
      )}
    </DialogTitle>
  )
}

interface ModalFooterProps extends ComponentProps<'footer'> {
  className?: string
}

function ModalFooter({ children, className, ...restProps }: ModalFooterProps) {
  return (
    <footer {...restProps} className={classNames('w-full border-t px-5 py-3', className)}>
      {children}
    </footer>
  )
}

interface ModalBodyProps extends ComponentProps<'section'> {
  className?: string
}

function ModalBody({ children, className, ...restProps }: ModalBodyProps) {
  return (
    <section {...restProps} className={classNames('flex-1 p-5 text-sm overflow-y-overlay', className)}>
      {children}
    </section>
  )
}

export const Modal = Object.assign(ModalComponent, {
  Panel: ModalPanel,
  Title: ModalTitle,
  Footer: ModalFooter,
  Body: ModalBody,
  Description: Description,
})

function ModalPanel({ className, children, ...restProps }: ComponentProps<typeof DialogPanel>) {
  const context = useContext(ModalContext)

  if (context === undefined) {
    throw new Error('Modal.Panel must be used within a Modal component')
  }

  const { loading, error } = context

  return (
    <QueryResult loading={loading} error={undefined}>
      <DialogPanel
        transition
        className={classNames(
          'transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in',
          className
        )}
        {...restProps}
      >
        {(bag) => (
          <QueryResult loading={false} error={error}>
            {typeof children === 'function' ? children(bag) : children}
          </QueryResult>
        )}
      </DialogPanel>
    </QueryResult>
  )
}
